import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  ChangeDetectionStrategy,
  PLATFORM_ID,
  Inject,
  OnChanges
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ImageOptimizationService } from '../../services/image-optimization.service';

@Component({
  selector: 'app-optimized-image',
  standalone: false,
  template: `
    <div class="optimized-image-container" [ngClass]="containerClass" [style.aspect-ratio]="aspectRatio">
      <!-- Progressive enhancement with low-quality placeholder -->
      <img
        *ngIf="placeholder && !imageLoaded"
        [src]="placeholder"
        [alt]="alt"
        class="placeholder-image"
        [ngClass]="cssClass"
        loading="eager"
      />
      
      <!-- Main optimized image with picture element for format support -->
      <picture #pictureElement>
        <!-- WebP sources for better compression -->
        <source
          *ngIf="webpSrcset"
          [srcset]="webpSrcset"
          [sizes]="sizes || defaultSizes"
          type="image/webp"
        />
        
        <!-- Fallback image -->
        <img
          #imageElement
          [src]="imageAttributes.src"
          [srcset]="imageAttributes.srcset"
          [sizes]="imageAttributes.sizes"
          [alt]="imageAttributes.alt"
          [loading]="imageAttributes.loading"
          [attr.fetchpriority]="imageAttributes.fetchpriority"
          [width]="imageAttributes.width"
          [height]="imageAttributes.height"
          [class]="'responsive-image ' + cssClass"
          (load)="onImageLoad($event)"
          (error)="onImageError($event)"
        />
      </picture>
      
      <!-- Loading skeleton -->
      <div 
        *ngIf="showSkeleton && !imageLoaded"
        class="image-skeleton"
        [style.aspect-ratio]="aspectRatio"
        [style.width.px]="width"
        [style.height.px]="height"
      ></div>
      
      <!-- Error state -->
      <div 
        *ngIf="imageError"
        class="image-error-state"
        [style.aspect-ratio]="aspectRatio"
        [style.width.px]="width"
        [style.height.px]="height"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.134 16.5c-.77.833.192 2.5 1.732 2.5z"/>
        </svg>
        <span>Failed to load image</span>
      </div>
    </div>
  `,
  styleUrls: ['./optimized-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OptimizedImageComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('imageElement') imageElement!: ElementRef<HTMLImageElement>;
  @ViewChild('pictureElement') pictureElement!: ElementRef<HTMLPictureElement>;

  @Input() src: string = '';
  @Input() alt: string = '';
  @Input() width: number = 640;
  @Input() height: number = 640;
  @Input() aspectRatio?: string;
  @Input() loading: 'eager' | 'lazy' = 'lazy';
  @Input() fetchpriority: 'high' | 'low' | 'auto' = 'auto';
  @Input() sizes: string = '';
  @Input() cssClass: string = '';
  @Input() containerClass: string = '';
  @Input() fallbackSrc: string = '';
  @Input() placeholder?: string;
  @Input() showSkeleton: boolean = true;
  @Input() quality: number = 80;

  // Responsive breakpoints
  @Input() breakpoints: number[] = [320, 640, 768, 1024, 1280, 1920];

  imageAttributes: any = {};
  imageLoaded = false;
  imageError = false;
  webpSrcset = '';
  defaultSizes = '(max-width: 640px) 100vw, (max-width: 1024px) 80vw, 1200px';

  private intersectionObserver?: IntersectionObserver;

  constructor(
    private imageOptimizationService: ImageOptimizationService,
    private elementRef: ElementRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit() {
    this.updateImageAttributes();
    if (isPlatformBrowser(this.platformId)) {
      this.generateResponsiveSources();
      this.setupIntersectionObserver();
    }
  }

  ngOnChanges() {
    this.updateImageAttributes();
    if (isPlatformBrowser(this.platformId)) {
      this.generateResponsiveSources();
    }
  }

  ngOnDestroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  private updateImageAttributes() {
    this.imageAttributes = this.imageOptimizationService.getImageAttributes(
      this.src,
      {
        width: this.width,
        height: this.height,
        alt: this.alt,
        loading: this.loading,
        fetchpriority: this.fetchpriority,
        sizes: this.sizes || this.defaultSizes
      }
    );
  }

  private generateResponsiveSources() {
    if (!this.src) return;

    // Generate WebP sources for better compression
    this.webpSrcset = this.breakpoints
      .map(width => {
        const url = this.imageOptimizationService.getResponsiveImageUrl(this.src, width, this.quality);
        return `${url} ${width}w`;
      })
      .join(', ');
  }

  private setupIntersectionObserver() {
    if (!isPlatformBrowser(this.platformId) || this.loading === 'eager') {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadImage();
            this.intersectionObserver?.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.01
      }
    );

    this.intersectionObserver.observe(this.elementRef.nativeElement);
  }

  private loadImage() {
    // Image will load automatically when srcset is set
    // This method can be used for additional loading logic if needed
  }

  onImageLoad(event: Event) {
    this.imageLoaded = true;
    this.imageError = false;

    if (this.imageElement?.nativeElement) {
      this.imageElement.nativeElement.classList.add('loaded');
    }
  }

  onImageError(event: Event) {
    this.imageError = true;
    this.imageLoaded = false;

    // Try fallback image if available
    if (this.fallbackSrc && event.target instanceof HTMLImageElement) {
      event.target.src = this.fallbackSrc;
    }
  }

  /**
   * Preload this image for better performance
   */
  preload(): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = this.imageAttributes.src || this.src;
    });
  }
}
